import { FC } from 'react';
import { IonButton, IonButtons, IonCard, IonCardContent, IonCol, IonGrid, IonIcon, IonRow } from '@ionic/react';
import { DqText } from '../dq-text';
import './ControllerSummaryDetails.scss';
import { Controller } from 'src/models/controller';
import { useIntl } from 'react-intl';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import { JobMessages } from 'src/intl/JobMessages';
import { Badge } from '../reusable-components/Badge';
import { pencil, arrowRedoOutline, link } from 'ionicons/icons';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { bleActions } from 'src/redux/ble/actions';

export interface ControllerSummaryDetailsProps {
  /**
   * The controller information to display
   */
  controller: Controller;
  /**
   * The name of the Live Job in progress.
   */
  liveJob?: string;
  /**
   * The firmware version of the controller
   */
  firmwareVersion: string;
  /**
   * The name of the organization the controller belongs to
   */
  organization: string;
  /**
   * The callback triggered when the card is clicked
   */
  onUpdateFirmware?: (e: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
  /**
   * The callback triggered when the reassign button is clicked
   */
  onReassignController?: (e: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
  /**
   * Whether or not the controller is connected
   */
  isControllerConnected?: boolean;
  /**
   *  The callback triggered when the update controller button is clicked
   */
  onUpdateController: (e: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
}

export const ControllerSummaryDetails: FC<ControllerSummaryDetailsProps> = ({
  liveJob,
  controller,
  firmwareVersion,
  organization,
  onUpdateFirmware,
  onReassignController,
  onUpdateController,
  isControllerConnected,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const formattedControllerLabel = formatMessage(ControllerMessages.controllerIdLabel, {
    physicalId: controller.physicalSerial,
  });

  return (
    <IonCard
      className={classNames(
        'controller-summary-details-card',
        isControllerConnected && 'controller-summary-card-connected',
      )}
    >
      <IonCardContent>
        <IonGrid>
          <IonRow className="controller-summary-heading-row">
            <IonCol>
              {isControllerConnected && <Badge isActive icon={link} color="secondary" isLarge />}
              <DqText className={isControllerConnected ? 'ion-margin-start' : ''} variant="h3">
                {formattedControllerLabel}
              </DqText>
            </IonCol>
            <IonCol size="auto">
              <IonButtons>
                {onReassignController && (
                  <IonButton className="icon-button" fill="outline" color="clear" onClick={onReassignController}>
                    <IonIcon icon={arrowRedoOutline} slot="icon-only" color="dark" size="small" />
                  </IonButton>
                )}
                {onUpdateController && (
                  <IonButton className="icon-button" fill="outline" onClick={onUpdateController}>
                    <IonIcon icon={pencil} slot="icon-only" color="dark" size="small" />
                  </IonButton>
                )}
              </IonButtons>
            </IonCol>
          </IonRow>
          {liveJob && (
            <>
              <IonRow>
                <DqText variant="body-strong">{liveJob}</DqText>
              </IonRow>
              <IonRow className="ion-margin-bottom">
                <DqText color="medium" variant="body-small">
                  {formatMessage(JobMessages.jobLiveNow)}
                </DqText>
              </IonRow>
            </>
          )}
          <IonRow>
            <DqText variant="body-strong">{firmwareVersion}</DqText>
          </IonRow>
          <IonRow className="ion-margin-bottom">
            <DqText color="medium" variant="body-small">
              {formatMessage(ControllerMessages.firmwareVersionLabel)}
            </DqText>
          </IonRow>
          <IonRow>
            <DqText variant="body-strong">{controller._id}</DqText>
          </IonRow>
          <IonRow className="ion-margin-bottom">
            <DqText color="medium" variant="body-small">
              {formatMessage(ControllerMessages.controllerIdentificationLabel)}
            </DqText>
          </IonRow>
          <IonRow>
            <DqText variant="body-strong">{organization}</DqText>
          </IonRow>
          <IonRow className="ion-margin-bottom">
            <DqText color="medium" variant="body-small">
              {formatMessage(ControllerMessages.organization)}
            </DqText>
          </IonRow>
          {controller.description && (
            <>
              <IonRow>
                <DqText variant="body-strong">{controller.description}</DqText>
              </IonRow>
              <IonRow className="ion-margin-bottom">
                <DqText color="medium" variant="body-small">
                  {formatMessage(ControllerMessages.descriptionLabel)}
                </DqText>
              </IonRow>
            </>
          )}
          {isControllerConnected && (
            <IonRow className="ion-margin-top">
              <IonCol>
                <IonButton expand="block" fill="outline" color="primary" onClick={onUpdateFirmware}>
                  {formatMessage(ControllerMessages.updateFirmware)}
                </IonButton>
              </IonCol>
              <IonCol>
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={e => {
                    e.stopPropagation();
                    dispatch(bleActions.openConnectControllerModal());
                  }}
                >
                  {formatMessage(ControllerMessages.changeController)}
                </IonButton>
              </IonCol>
            </IonRow>
          )}
        </IonGrid>
      </IonCardContent>
    </IonCard>
  );
};
